import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import Discover from '../../../../common/redux/actions/DiscoverActions';
import { get, map } from 'lodash';
import { throttle } from 'throttle-debounce';
import { IColorGroup } from './color-group-setting.interfaces';
import { generateCustomColorGroup, VIZ_OPTION_IDS } from '../../../../common';
import {
  useOpenDiscoveryPresentStateSelector,
  useOpenVizSelector,
  useVizOptionSelector,
} from '../../../../common/redux/selectors/viz-selector.hook';
import { generateColorForColorGroup } from './color-group-setting.utils';
import { Viz } from '../../../VizUtil';

export const useColorGroupSetting = ({
  item: legendData,
  vizId,
  customColors,
}: IColorGroup) => {
  const dispatch = useDispatch();
  const open = useOpenDiscoveryPresentStateSelector({
    discoveryId: vizId,
  });
  const legendGroupData = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.legendGroupMetadata,
    defaultValue: [],
  });
  const throttledDispatchRef = useRef(null);
  const { label, id, fields = [] } = legendData;
  const [colorGroupLabel, setColorGroupLabel] = useState(label);

  const viz = useOpenVizSelector({
    discoveryId: vizId,
  });
  const fieldDisplayNamesDictionary = useMemo(() => {
    const allFields = Viz.getAllAvailableFields(viz);
    return Viz.createFieldDictionary('displayName', allFields);
  }, [viz]);

  useEffect(() => {
    setColorGroupLabel(label);
  }, [label]);

  useEffect(() => {
    throttledDispatchRef.current = throttle(300, (value: string) => {
      dispatch(Discover.updateVizLegendGroupLabelData(vizId, id, value));
    });
  }, [dispatch, id, vizId]);

  const onColorGroupChange = useCallback(
    (newColor: string) => {
      const newColors = {
        ...customColors,
        colorGroup: {
          ...(customColors?.colorGroup || {}),
          ...generateCustomColorGroup({
            colorGroupId: id,
            fields,
            groupColor: newColor,
          }),
        },
      };

      dispatch(Discover.updateCustomColors(vizId, newColors));
    },
    [customColors, id, fields, dispatch, vizId],
  );

  const color = generateColorForColorGroup({
    legendGroupData,
    viz: open?.viz,
    currentId: id,
    fields,
  });
  const selColor = get(customColors, [id], color);

  const onLegendLabelChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e?.target?.value;
    setColorGroupLabel(newValue);
    throttledDispatchRef.current(newValue);
  };

  const onFieldsSelectionSave = useCallback(
    (selectedDisplayNames: string[]) => {
      const selectedFields = map(selectedDisplayNames, fieldDisplayName =>
        get(
          fieldDisplayNamesDictionary,
          [fieldDisplayName, 'name'],
          fieldDisplayName,
        ),
      );

      dispatch(
        Discover.updateVizLegendGroupFieldsData(vizId, id, selectedFields),
      );
    },
    [dispatch, fieldDisplayNamesDictionary, id, vizId],
  );

  const removeColorGroup = useCallback(() => {
    dispatch(Discover.removeVizLegendGroupData(vizId, id));
  }, [dispatch, id, vizId]);

  return {
    selColor,
    onColorGroupChange,
    onLegendLabelChange,
    onFieldsSelectionSave,
    removeColorGroup,
    colorGroupLabel,
  };
};
